import * as React from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'

import { Icon, IconButton, Typography } from '~components/internal'
import { type AssistantTableData } from '~utils/chartUtils'
import { downloadCsv } from '~utils/csvUtils'
import { markdownLinksToHtml, sanitizeAndProcessLinks } from '~utils/htmlUtils'
import { isNumeric } from '~utils/numberUtils'
import { slugify } from '~utils/textUtils'

import './AssistantTableRenderer.scss'

interface Props {
  table: AssistantTableData
}

const AssistantTableRenderer: React.FC<Props> = ({ table }) => {
  const { title, columns, rows } = table

  const handleDownload = (): void => {
    const filename = `${slugify(title ?? 'table', 'table')}.csv`
    downloadCsv(columns, rows, filename)
  }

  return (
    <Box className='assistant-table'>
      <Box className='assistant-table__content'>
        {title && (
          <Typography variant='h6' className='assistant-table__title'>
            {title}
          </Typography>
        )}
        <TableContainer className='assistant-table__container'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {columns.map((col, i) => (
                  <TableCell key={i} className='assistant-table__header-cell'>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className='assistant-table__empty-cell'>
                    <Typography className='assistant-table__no-data'>No data available</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, ri) => (
                  <TableRow key={ri} className='assistant-table__body-row'>
                    {row.map((cell, ci) => {
                      const isEmpty = cell === '' || cell === null || cell === undefined
                      const numeric = !isEmpty && isNumeric(cell)
                      return (
                        <TableCell
                          key={ci}
                          className={[
                            'assistant-table__body-cell',
                            numeric ? 'assistant-table__body-cell--numeric' : '',
                            isEmpty ? 'assistant-table__body-cell--empty' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          dangerouslySetInnerHTML={{
                            __html: isEmpty
                              ? '—'
                              : sanitizeAndProcessLinks(markdownLinksToHtml(String(cell))),
                          }}
                        />
                      )
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box className='assistant-table__download-row'>
        <Tooltip title='Download as CSV'>
          <IconButton size='small' onClick={handleDownload}>
            <Icon name='DownloadIcon' fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default AssistantTableRenderer
